import { useState } from 'react';
import { Badge, Tooltip, Collapse, ActionIcon } from '@mantine/core';
import { IconTrash, IconChevronDown, IconChevronUp, IconPuzzle } from '@tabler/icons-react';
import type { SelectedChromeExtensionSelection } from '~/server/logic/prisma/chrome-extension-select';
import { api } from '~/trpc-extra/api';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';

export const InstalledExtensionsStep = ({
  testContentId,
  extensions,
  readonly = false,
}: {
  testContentId: string;
  extensions: SelectedChromeExtensionSelection[];
  readonly?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const trpcUtils = api.useUtils();

  const { mutateAsync: removeExtension } = api.testContent.removeChromeExtension.useMutation({
    onSuccess: async () => {
      await trpcUtils.testContent.get.invalidate({ id: testContentId });
    },
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to remove the extension from the test.',
        color: 'red',
      });
    },
  });

  return (
    <div className="mx-10 mb-3 rounded-lg border-2 bg-[#fafafa] dark:border-gray-600 dark:bg-stone-800">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full cursor-pointer items-center justify-between p-4 text-left"
      >
        <div className="flex items-center gap-2">
          <IconPuzzle size={16} />
          <p className="text-sm font-medium">Installed Extensions</p>
          <Badge className="ml-2">{extensions.length}</Badge>
        </div>
        {isOpen ?
          <IconChevronUp size={16} />
        : <IconChevronDown size={16} />}
      </div>
      <Collapse in={isOpen}>
        <div className="space-y-4 px-4 pb-4">
          {extensions.map((extension) => (
            <div
              key={extension.id}
              className="group flex items-center justify-between rounded-md"
            >
              <div className="flex items-center gap-2">
                <img
                  src={extension.chromeExtension.iconURL ?? '/assets/placeholder.png'}
                  alt={`${extension.chromeExtension.name} logo`}
                  className="h-8 w-8 rounded-lg object-cover"
                />
                <div>
                  <p className="text-sm font-medium">{extension.chromeExtension.name}</p>
                  <p className="text-muted-foreground text-xs">
                    {extension.mode === 'PINNED' && extension.chromeExtensionVersion ?
                      `v${extension.chromeExtensionVersion.version}`
                    : 'Latest'}
                  </p>
                </div>
              </div>
              {!readonly && (
                <Tooltip
                  label={`Remove ${extension.chromeExtension.name}`}
                  withArrow
                >
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      modals.openConfirmModal({
                        title: 'Uninstall Extension',
                        children: (
                          <p>
                            Are you sure you want to uninstall {extension.chromeExtension.name}{' '}
                            {!!extension.chromeExtensionVersion?.version &&
                              `v${extension.chromeExtensionVersion.version}`}{' '}
                            from your virtual browser?
                          </p>
                        ),
                        centered: true,
                        confirmProps: { color: 'red' },
                        labels: {
                          confirm: 'Uninstall',
                          cancel: 'Cancel',
                        },
                        onConfirm: async () => {
                          await removeExtension({ testContentId, chromeExtensionId: extension.chromeExtensionId });
                        },
                      });
                    }}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
            </div>
          ))}
        </div>
      </Collapse>
    </div>
  );
};
